import { UsageWordSurface } from "#usage-word-surface";
import { useRouteBodyState } from "@-/dev.improve.haniverse.lib.navigation.route-body-state";
import type { InfoMorphemeSlice } from "@-/dev.improve.haniverse.lib.morpheme.entities";
import type { MorphemeInfoPanelAccordionSectionDescriptor } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._components.morpheme-info-panel-accordion";
import { Text } from "@-/dev.improve.haniverse.components.ui.text";
import { useDictionaryWordsQueryOptions } from "@-/dev.improve.haniverse.lib.morpheme.word.read";
import { useQuery } from "@-/com.npmjs.tanstack__react-query";
import { useMemo, type ReactNode } from "react";
import { Pressable, View } from "@-/com.npmjs.react-native";
import { hasLength } from "@-/com.npmjs.ts-array-length";

export function useMorphemeInfoPanelUsageWordsSection({
  infoBackCardSlice,
}: {
  infoBackCardSlice: InfoMorphemeSlice;
}): MorphemeInfoPanelAccordionSectionDescriptor | null {
  const [visibleCount, setVisibleCount] = useRouteBodyState("usage-words-visible-count", 10);
  const graphemeCard = infoBackCardSlice.morphemeType === "grapheme" ? infoBackCardSlice : null;
  const {
    data: words,
    error,
    isLoading,
  } = useQuery({
    ...useDictionaryWordsQueryOptions(graphemeCard?.graphemeId ?? -1),
    enabled: graphemeCard !== null,
  });

  // The character's own word (e.g. 口 as a word) is pinned first, then the rest sort by rank
  // (known ranks ascending first, unknown last). Deduplicated by word id — the join on
  // word_simplified_grapheme can return the same word multiple times when the grapheme appears at
  // multiple indices in the word.
  const characterWord = graphemeCard?.grapheme;
  const sortedWords = useMemo(() => {
    if (!words) {
      return null;
    }
    const seenIds = new Set<number>();
    const deduped: typeof words = [];
    for (const word of words) {
      if (seenIds.has(word.id)) {
        continue;
      }
      seenIds.add(word.id);
      deduped.push(word);
    }
    return deduped.sort((a, b) => {
      if (a.word === characterWord || b.word === characterWord) {
        return a.word === characterWord ? -1 : 1;
      }
      if (a.rank === null && b.rank === null) {
        return 0;
      }
      if (a.rank === null) {
        return 1;
      }
      if (b.rank === null) {
        return -1;
      }
      return a.rank - b.rank;
    });
  }, [characterWord, words]);

  if (graphemeCard === null) {
    return null;
  }

  let body: ReactNode;
  if (error) {
    body = (
      <Text className="p-4 text-sm text-destructive">Failed to load words: {error.message}</Text>
    );
  } else if (!sortedWords) {
    body = null;
  } else if (hasLength(sortedWords, 0)) {
    body = (
      <View className="p-4">
        <Text className="text-sm text-muted-foreground italic">No words found.</Text>
      </View>
    );
  } else {
    body = (
      <View className="gap-y-2 px-2.25 py-2">
        {sortedWords.slice(0, visibleCount).map((word) => (
          <View key={word.id} testID={`usage-word-${word.id}`}>
            <UsageWordSurface word={word} />
          </View>
        ))}
        {visibleCount < sortedWords.length ? (
          <Pressable
            accessibilityRole="button"
            className="p-2"
            onPress={() => setVisibleCount((count) => count + 10)}
          >
            <Text className="text-center text-primary">Show more words</Text>
          </Pressable>
        ) : null}
      </View>
    );
  }

  return {
    bodyKey: "morpheme-info-usage-words-body",
    headerKey: "morpheme-info-usage-words-header",
    // Reading aids now belong to the sentence accordions. An empty action strip keeps this
    // section open without displaying global toggles that the accordions don't use.
    headerActions: [],
    iconName: "LuWholeWord",
    isLoading,
    isOpen: true,
    onToggle: () => {},
    renderBody: () => <View>{body}</View>,
    title: "Words in context",
  };
}
